import type { ComponentChildren } from "preact";
import type { CSSProperties, JSX } from "preact/compat";
import { useEffect } from "preact/hooks";

import { disableInput } from "../Utility";
import styles from "./BaseModal.module.css";
import { clsx } from "./clsx";

export type BaseModalProps = {
  open: boolean;
  disableBackdropBlur?: boolean;
  disableBackdrop?: boolean;
  onClose?: (event: object, reason: "backdropClick" | "escapeKeyDown") => void;
  children?: ComponentChildren;
} & Omit<JSX.HTMLAttributes<HTMLDivElement>, "onClose">;

let globalIndexIncrement = 0;

export const BaseModal = ({
  open,
  disableBackdropBlur,
  disableBackdrop,
  onClose,
  children,
  style = {} as CSSProperties,
  ...rest
}: BaseModalProps) => {
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        disableInput(e);
        onClose?.(e, "escapeKeyDown");
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const backdropClasses = disableBackdrop
    ? styles.transparentBackground
    : clsx(styles.backdrop, styles.fadeIn, !disableBackdropBlur ? styles.blur : undefined);

  const handleMouseDown = (e: MouseEvent) => {
    if (e.target === e.currentTarget) {
      // only handle if directly clicked om the backdrop
      disableInput(e);
      onClose?.(e, "backdropClick");
    }
  };

  const zIndex = (style as CSSProperties)?.zIndex ?? 1300 + globalIndexIncrement++;

  return (
    <div
      role="presentation"
      data-testid="backdrop"
      className={backdropClasses}
      onMouseDown={handleMouseDown}
      style={{ zIndex, ...(style as CSSProperties) }}
      {...rest}
    >
      <div className={clsx("supportsRtl", styles.modalContent)}>{children}</div>
    </div>
  );
};
